import { ModalOverlayPrimaryHeaderBackground } from '@shared/components/modal-overlay';

import { PsFlexTimeActivitiesService } from '../services/forms/ps-activities.service';
import { PsArchiveService } from '../services/forms/ps-archive.service';
import { PsAutomationResetService } from '../services/forms/ps-automation-reset.service';
import { PsBehaviorService } from '../services/forms/ps-behavior.service';
import { PsChallengeService } from '../services/forms/ps-challenge.service';
import { PsCheckInService } from '../services/forms/ps-checkin.service';
import { PsEventService } from '../services/forms/ps-event.service';
import { PsGroupService } from '../services/forms/ps-group.service';
import { PsHallPassService } from '../services/forms/ps-hall-pass.service';
import { PsListMembershipService } from '../services/forms/ps-list-membership.service';
import { PsPointsRewardService } from '../services/forms/ps-points-reward.service';
import { PsPointsService } from '../services/forms/ps-points.service';
import { PsAutomationService } from '../services/forms/ps-reset-automation.service';
import { PsStandAloneService } from '../services/forms/ps-standalone.service';
import { PsTicketsService } from '../services/forms/ps-tickets.service';
import {
  PsAutomationResetData,
  PsBehaviorsData,
  PsChallengeData,
  PsCheckinData,
  PsEventData,
  PsFilterState,
  PsFilters,
  PsFlexTimeActivitiesData,
  PsGroupData,
  PsHallPassData,
  PsMembershipListData,
  PsMultiAutomationResetData,
  PsPointsData,
  PsPointsRewardData,
  PsSearchTools,
  PsStandAloneConfig,
  PsTeacherListData,
  PsTicketsData,
} from '../types';
import { getPeopleSelectorInjectionToken, validatePeopleSelectorFormConfig } from '../utils';

export enum PeopleSelectorRoutes {
  ROOT = 'people-selector',
}

export enum PeopleSelectorMessages {
  SELECT_ALL = 'Select All',
  DESELECT_ALL = 'Deselect All',
  SELECT_ALL_TOGGLE = 'Select All / Clear',
  CANCEL_BUTTON = 'Cancel',
  SEARCH_BUTTON = 'Search',
  ERRORS_BUTTON = 'View Errors',
}

export enum PeopleSelectorTooltips {
  FLEXTIME_REGISTER = 'Registered students are able to change activity',
  FLEXTIME_ASSIGN = "Assigned students can't unregister themselves",
}

export const PS_FORM_FILTER_DEFAULT_STATE: PsFilterState = {
  keywords: '',
  grades: [],
  lists: [],
  roles: [],
  groups: [],
  schools: [],
  fileUploadedPeople: [],
};

export const PS_CAMERA_REOPEN_DELAY = 1500;

export const PS_CAMERA_NOT_FOUND_REOPEN_DELAY = 1500;

export const DEFAULT_PS_SEARCH_TOOLS: PsSearchTools[] = ['barcode', 'camera', 'file'];

export const DEFAULT_PS_FORM_FILTERS: PsFilters[] = ['groups', 'lists', 'grades', 'keywords', 'roles'];

export const PEOPLE_SELECTOR_FORMS = {
  Standalone: validatePeopleSelectorFormConfig({
    path: 'standalone',
    injectionToken: getPeopleSelectorInjectionToken('standalone'),
    service: PsStandAloneService,
    data: {} as PsStandAloneConfig,
    pages: {
      select: {
        path: 'select',
        type: 'search',
        text: {
          title: 'Select',
          submit: 'Select',
        },
      },
    },
  }),
  'Hall Pass': validatePeopleSelectorFormConfig({
    path: 'hall-pass',
    injectionToken: getPeopleSelectorInjectionToken('hall-pass'),
    service: PsHallPassService,
    theme: {
      primaryButton: 'yellow',
      headerBg: ModalOverlayPrimaryHeaderBackground.PINK,
    },
    data: {} as PsHallPassData,
    pages: {
      assign: {
        path: 'assign',
        type: 'search',
        text: {
          title: 'Assign',
          submit: 'Assign',
        },
      },
    },
  }),
  'FlexTime Activity': validatePeopleSelectorFormConfig({
    path: 'flextime-activities',
    injectionToken: getPeopleSelectorInjectionToken('flextime-activities'),
    service: PsFlexTimeActivitiesService,
    theme: {
      primaryButton: 'kiwi',
      headerBg: ModalOverlayPrimaryHeaderBackground.ALT_TEAL,
    },
    data: {} as PsFlexTimeActivitiesData,
    pages: {
      assign: {
        path: 'assign',
        type: 'search',
        text: {
          title: 'Assign',
          submit: 'Assign',
        },
        tooltip: PeopleSelectorTooltips.FLEXTIME_ASSIGN,
      },
      register: {
        path: 'register',
        type: 'search',
        text: {
          title: 'Register',
          submit: 'Register',
        },
        tooltip: PeopleSelectorTooltips.FLEXTIME_REGISTER,
      },
      remove: {
        path: 'remove',
        type: 'list',
        text: {
          title: 'Remove',
          submit: 'Remove',
        },
        searchTools: [],
        filters: ['keywords'],
      },
      checkin: {
        path: 'checkin',
        type: 'list',
        text: {
          title: 'Check In',
          submit: 'Check In',
        },
        searchTools: ['file', 'barcode', 'camera'],
        filters: ['keywords'],
      },
    },
  }),
  Archive: validatePeopleSelectorFormConfig({
    path: 'archive',
    injectionToken: getPeopleSelectorInjectionToken('archive'),
    service: PsArchiveService,
    pages: {
      add: {
        path: 'add',
        type: 'search',
        text: {
          title: 'Add',
          submit: 'Archive',
        },
      },
    },
  }),
  Behavior: validatePeopleSelectorFormConfig({
    path: 'behavior',
    injectionToken: getPeopleSelectorInjectionToken('behavior'),
    service: PsBehaviorService,
    theme: {
      primaryButton: 'pink',
      headerBg: ModalOverlayPrimaryHeaderBackground.GREEN,
    },
    data: {} as PsBehaviorsData,
    pages: {
      assign: {
        path: 'assign',
        type: 'search',
        text: {
          title: 'Assign',
          submit: 'Assign',
        },
      },
    },
  }),
  'Check In': validatePeopleSelectorFormConfig({
    path: 'checkin',
    injectionToken: getPeopleSelectorInjectionToken('checkin'),
    service: PsCheckInService,
    theme: {
      primaryButton: 'orange',
      headerBg: ModalOverlayPrimaryHeaderBackground.TEAL,
    },
    data: {} as PsCheckinData,
    pages: {
      add: {
        path: 'add',
        type: 'search',
        text: {
          title: 'Add',
          submit: 'Add',
        },
      },
    },
  }),
  Group: validatePeopleSelectorFormConfig({
    path: 'group',
    injectionToken: getPeopleSelectorInjectionToken('group'),
    service: PsGroupService,
    data: {} as PsGroupData,
    pages: {
      add: {
        path: 'add',
        type: 'search',
        text: {
          title: 'Add',
          submit: 'Add',
        },
      },
      remove: {
        path: 'remove',
        type: 'list',
        text: {
          title: 'Remove',
          submit: 'Remove',
        },
        searchTools: [],
        filters: ['keywords'],
      },
    },
  }),
  'List Membership': validatePeopleSelectorFormConfig({
    path: 'list-membership',
    injectionToken: getPeopleSelectorInjectionToken('list-membership'),
    service: PsListMembershipService,
    data: {} as PsMembershipListData,
    pages: {
      add: {
        path: 'add',
        type: 'search',
        text: {
          title: 'Add',
          submit: 'Add',
        },
      },
      remove: {
        path: 'remove',
        type: 'list',
        text: {
          title: 'Remove',
          submit: 'Remove',
        },
        searchTools: ['file'],
        filters: ['keywords'],
      },
    },
  }),
  Points: validatePeopleSelectorFormConfig({
    path: 'points',
    injectionToken: getPeopleSelectorInjectionToken('points'),
    service: PsPointsService,
    data: {} as PsPointsData,
    pages: {
      redeem: {
        path: 'redeem',
        type: 'search',
        text: {
          title: 'Redeem',
          submit: 'Assign',
        },
      },
    },
  }),
  Automation: validatePeopleSelectorFormConfig({
    path: 'automation',
    injectionToken: getPeopleSelectorInjectionToken('automation'),
    service: PsAutomationService,
    data: {} as PsAutomationResetData,
    pages: {
      reset: {
        path: 'reset',
        type: 'search',
        text: {
          title: 'Reset',
          submit: 'Reset',
        },
      },
    },
  }),
  Challenge: validatePeopleSelectorFormConfig({
    path: 'challenge',
    injectionToken: getPeopleSelectorInjectionToken('challenge'),
    service: PsChallengeService,
    data: {} as PsChallengeData,
    pages: {
      invite: {
        path: 'invite',
        type: 'search',
        text: {
          title: 'Invite',
          submit: 'Invite',
        },
      },
      invited: {
        path: 'invited',
        type: 'list',
        text: {
          title: 'Invited',
          submit: 'Invited',
        },
        isReadOnly: true,
      },
    },
  }),
  Event: validatePeopleSelectorFormConfig({
    path: 'event',
    injectionToken: getPeopleSelectorInjectionToken('event'),
    service: PsEventService,
    data: {} as PsEventData,
    pages: {
      checkin: {
        path: 'checkin',
        type: 'search',
        text: {
          title: 'Check in',
          submit: 'Check in',
        },
      },
      checkout: {
        path: 'checkout',
        type: 'list',
        text: {
          title: 'Check out',
          submit: 'Check out',
        },
      },
      invite: {
        path: 'invite',
        type: 'search',
        text: {
          title: 'Invite',
          submit: 'Invite',
        },
      },
    },
  }),
  'Points Reward': validatePeopleSelectorFormConfig({
    path: 'points-reward',
    injectionToken: getPeopleSelectorInjectionToken('points-reward'),
    service: PsPointsRewardService,
    data: {} as PsPointsRewardData,
    pages: {
      redeem: {
        path: 'redeem',
        type: 'search',
        text: {
          title: 'Redeem',
          submit: 'Redeem',
        },
        maxSelection: 1,
      },
    },
  }),
  'Automation Reset': validatePeopleSelectorFormConfig({
    path: 'automation-reset',
    injectionToken: getPeopleSelectorInjectionToken('automation-reset'),
    service: PsAutomationResetService,
    data: {} as PsMultiAutomationResetData,
    pages: {
      reset: {
        path: 'reset',
        type: 'search',
        text: {
          title: 'Update',
          submit: 'Update',
        },
      },
    },
  }),
  Tickets: validatePeopleSelectorFormConfig({
    path: 'tickets',
    injectionToken: getPeopleSelectorInjectionToken('tickets'),
    service: PsTicketsService,
    data: {} as PsTicketsData,
    pages: {
      add: {
        path: 'add',
        type: 'search',
        text: {
          title: 'Add',
          submit: 'Add',
        },
      },
    },
  }),
} as const;
